import { useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * NavigationGuard component that prevents accidental navigation away from the app
 * Shows confirmation dialog when user tries to:
 * - Click browser back button
 * - Close tab/window
 * - Navigate to external URL
 */
const NavigationGuard = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Handle browser back/forward button
  useEffect(() => {
    // Check if user is logged in
    const isLoggedIn = localStorage.getItem('token') && localStorage.getItem('user');
    
    if (!isLoggedIn) {
      // Don't show confirmation on login page
      return;
    }

    // Add history entry to detect back button
    const handlePopState = (event) => {
      const confirmLeave = window.confirm(
        'Are you sure you want to go back? You may lose unsaved changes.'
      );
      
      if (!confirmLeave) {
        // Push current location back to history to prevent navigation
        window.history.pushState(null, '', window.location.pathname);
      }
    };

    // Push current state to history
    window.history.pushState(null, '', window.location.pathname);
    
    // Listen for popstate (back/forward button)
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [location.pathname]);

  // Handle tab close/refresh
  useEffect(() => {
    const isLoggedIn = localStorage.getItem('token') && localStorage.getItem('user');
    
    if (!isLoggedIn) {
      return;
    }

    const handleBeforeUnload = (event) => {
      // Modern browsers require returnValue to be set
      event.preventDefault();
      event.returnValue = '';
      return '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  return null; // This component doesn't render anything
};

export default NavigationGuard;
